// src/pages/InsideScienceLearn.jsx
import React, { useLayoutEffect, useRef, useState, useEffect } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
gsap.registerPlugin(ScrollTrigger);

function SafeImg({ src, alt = "", className }) {
  const [ok, setOk] = useState(true);
  return ok ? (
    <img className={className} src={src} alt={alt} loading="lazy" decoding="async" onError={() => setOk(false)} />
  ) : (
    <div className={`${className} img-fallback`} aria-hidden />
  );
}

function Arrow() {
  return (
    <svg className="faq1-arrow" width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M6 9l6 6 6-6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function FAQItem({ id, q, children }) {
  const [open, setOpen] = React.useState(false);
  const bodyRef = React.useRef(null);

  React.useEffect(() => {
    const el = bodyRef.current;
    if (!el) return;
    const h = el.scrollHeight || 0;
    if (open) {
      gsap.fromTo(el, { height: 0 }, { height: h, duration: 0.35, ease: "power2.out", onComplete: () => (el.style.height = "auto") });
    } else {
      gsap.fromTo(el, { height: h }, { height: 0, duration: 0.28, ease: "power2.inOut" });
    }
  }, [open]);

  return (
    <div className={`faq1-item${open ? " open" : ""}`}>
      <button className="faq1-q" aria-expanded={open} aria-controls={`${id}-a`} onClick={() => setOpen(!open)}>
        <span className="faq1-qtxt">{q}</span>
        <Arrow />
      </button>
      <div id={`${id}-a`} ref={bodyRef} className="faq1-a" role="region" aria-hidden={!open}>
        <div className="faq1-a-inner">
          <div className="measure">{children}</div>
        </div>
      </div>
    </div>
  );
}


export default function DopeShotInfo() {
  const rootRef = useRef(null);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      const prefersReduced =
        typeof window !== "undefined" &&
        window.matchMedia &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches;

      gsap.set(".page-progress .bar", { scaleX: 0, transformOrigin: "0% 50%" });
      ScrollTrigger.create({
        start: 0,
        end: "max",
        onUpdate: (s) => gsap.set(".page-progress .bar", { scaleX: s.progress })
      });


      gsap.utils.toArray(".faq1-item").forEach((el) => {
        gsap.from(el, {
          y: 14,
          autoAlpha: 0,
          duration: 0.45,
          ease: "power3.out",
          scrollTrigger: { trigger: el, start: "top 120%", once: true }
        });
      });

      gsap.utils.toArray("[data-animate]").forEach((wrap, idx) => {
        const kids = wrap.querySelectorAll(".reveal:not(.no-batch)");
        if (!kids.length) return;
        gsap.from(kids, {
          y: 22,
          autoAlpha: 0,
          filter: "blur(8px)",
          clipPath: "inset(8% 0 10% 0 round 18px)",
          duration: 0.55 + (idx % 3) * 0.04,
          stagger: 0.08,
          ease: "power3.out",
          scrollTrigger: { trigger: wrap, start: "top 86%", once: true }
        });
      });

      gsap.utils.toArray(".headline").forEach((h) => {
        ScrollTrigger.create({
          trigger: h,
          start: "top 92%",
          once: true,
          onEnter: () => h.classList.add("reveal-line")
        });
      });

      if (!prefersReduced) {
        gsap.utils.toArray(".parallax").forEach((el, i) => {
          const setY = gsap.quickSetter(el, "yPercent");
          ScrollTrigger.create({
            trigger: el,
            start: "top bottom",
            end: "bottom top",
            scrub: true,
            onUpdate: (s) => setY(gsap.utils.mapRange(0, 1, i % 2 ? -4 : -2, i % 2 ? 4 : 2, s.progress))
          });
        });
      }

      gsap.utils.toArray(".card, .ph").forEach((el) => {
        ScrollTrigger.create({
          trigger: el,
          start: "top 88%",
          once: true,
          onEnter: () => el.classList.add("sheen-run")
        });
      });

      const mm = typeof window !== "undefined" && window.matchMedia ? window.matchMedia("(pointer: fine)") : null;
      if (mm && mm.matches) {
        const tiltCards = gsap.utils.toArray(".tilt");
        tiltCards.forEach((card) => {
          let raf = 0;
          const onMove = (e) => {
            if (raf) return;
            raf = requestAnimationFrame(() => {
              const r = card.getBoundingClientRect();
              const x = (e.clientX - r.left) / r.width - 0.5;
              const y = (e.clientY - r.top) / r.height - 0.5;
              card.style.setProperty("--rx", (y * -6).toFixed(2));
              card.style.setProperty("--ry", (x * 8).toFixed(2));
              raf = 0;
            });
          };
          const reset = () => {
            card.style.setProperty("--rx", "0");
            card.style.setProperty("--ry", "0");
          };
          card.addEventListener("mousemove", onMove);
          card.addEventListener("mouseleave", reset);
        });
      }

      const steps = document.querySelector(".imp-steps");
      const fill = document.querySelector(".imp-rail-fill");
      if (steps && fill) {
        gsap.set(fill, { scaleY: 0, transformOrigin: "0% 0%" });
        ScrollTrigger.create({
          trigger: steps,
          start: "top 70%",
          end: "bottom 30%",
          onUpdate: (s) => gsap.set(fill, { scaleY: s.progress })
        });

        gsap.utils.toArray(".imp-step").forEach((step) => {
          const dot = step.querySelector(".imp-dot");
          const card = step.querySelector(".imp-card");
          gsap.from(card, {
            y: 14,
            autoAlpha: 0,
            duration: 0.5,
            ease: "power3.out",
            scrollTrigger: { trigger: step, start: "top 85%", once: true }
          });
          ScrollTrigger.create({
            trigger: step,
            start: "top 60%",
            end: "bottom 60%",
            onEnter: () => step.classList.add("is-active"),
            onEnterBack: () => step.classList.add("is-active"),
            onLeave: () => step.classList.remove("is-active"),
            onLeaveBack: () => step.classList.remove("is-active")
          });
          if (dot) {
            ScrollTrigger.create({
              trigger: step,
              start: "top 85%",
              once: true,
              onEnter: () => gsap.fromTo(dot, { scale: 0.65 }, { scale: 1, duration: 0.28, ease: "back.out(3)" })
            });
          }
        });
      }

      const belief = document.querySelector(".belief-banner");
      if (belief) {
        const tl = gsap.timeline({
          scrollTrigger: { trigger: belief, start: "top 86%", once: true }
        });
        tl.fromTo(
          ".belief-figure",
          { y: 18, scale: 0.985, autoAlpha: 0, filter: "blur(6px)" },
          { y: 0, scale: 1, autoAlpha: 1, filter: "blur(0px)", duration: 0.7, ease: "power3.out", immediateRender: false, clearProps: "filter" }
        ).fromTo(
          ".belief-panel",
          { y: 20, autoAlpha: 0, filter: "blur(8px)" },
          { y: 0, autoAlpha: 1, filter: "blur(0px)", duration: 0.55, ease: "power3.out", immediateRender: false, clearProps: "filter" },
          "-=0.25"
        );
      }

      const refresh = () => ScrollTrigger.refresh();
      window.addEventListener("load", refresh, { once: true });
      requestAnimationFrame(refresh);
    }, rootRef);

    return () => ctx.revert();
  }, []);

  useEffect(() => {
    const titles = Array.from(document.querySelectorAll(".headline"));
    if (!titles.length) return;
    const tick = () => {
      titles.forEach((t, i) => {
        t.classList.remove("shine");
        void t.offsetWidth;
        setTimeout(() => t.classList.add("shine"), i * 220);
      });
    };
    tick();
    const id = setInterval(tick, 9000);
    return () => clearInterval(id);
  }, []);

  return (
    <main className="dsl v33" ref={rootRef}>
      
      <div className="bg" aria-hidden>
        <div className="bg-flow" />
        <div className="bg-dots" />
        <div className="bg-noise" />
      </div>
    

      {/* SECTION 1 */}
      <section className="sec">
        <div className="wrap" data-animate>
          <h2 className="headline reveal">Made up of Maltodextrin, Glucose, Fructose, Sucrose, Sea Salt and Citric Acid.</h2>

          <div className="chips reveal">
            
            {/*<span className="chip">Maltodextrin</span>
            <span className="chip">Glucose</span>
            <span className="chip">Fructose</span>
            <span className="chip">Sucrose</span>
            <span className="chip">Sea&nbsp;Salt</span>
            <span className="chip">Citric&nbsp;Acid</span>*/}
          </div> 

          <div className="grid-2">
            <div className="col reveal">
              <div className="measure">
                <br></br>
                <p>These four carbohydrates together, referred to as MTC (multiple transportable carbohydrates) have shown a 20 to 50 percent increase in exogenous carbohydrate oxidation verse consumption of single carbohydrate sources.</p>
                <p>MTC’s have also shown to decrease gastrointestinal distress vs consumption of a single carbohydrate source.</p>
              </div>
            </div>

            <div className="col reveal">
              <div className="media-grid">
                <figure className="ph wide parallax tilt"><SafeImg className="img" src="/DSCF0024.webp" /></figure>
              </div>
            </div>
          </div>
        </div>
      </section>

      <hr className="hr-shine" />

      {/* SECTION 2 
      <section className="sec alt">
        <div className="wrap" data-animate>
          <div className="grid-2 reverse">
            <div className="col reveal">
              <div className="media-stack">
                <figure className="ph tall parallax tilt"><SafeImg className="img" src="/IMG_0850.png" /></figure>
                <figure className="ph parallax tilt"><SafeImg className="img" src="/Front-and-Back-Mock-Up-4.png" /></figure>
              </div>
            </div>

            <div className="col reveal">
              <article className="content-card card tilt">
                <h3 className="headline sub">Why not 1:1 or 1:0.8 glucose: fructose formulation.</h3>
                <div className="measure">
                  <p>The research on this formulation is non-conclusive in my opinion and this ratio makes the formulation so sweet that easily consuming 100 grams per hour is literally breathtaking for many people (including myself).   These ratios don’t take into account the Dextrose Equivalent (D.E.) of different maltodextrins.  Dextrose is the technical term for Glucose.  Dextrose Equivalent is the degree of hydrolysis (breakdown) of the starch.  So maltodextrin, although a complex carbohydrate also has a varying percentage of Dextrose/Glucose equivalents, so some maltodextrin has low DE and some high DE.  We choose the highest DE equivalent maltodextrin which acts more like a Multiple Transportable Carbohydrate vs a single carbohydrate.  When we mix our maltodextrin with dextrose/glucose , fructose and sucrose, we believe we get the holy grail of sports nutrition an MTC product with a high glucose percentage along with high solubility and low sweetness.   This is the secret to easily being able to get the calories in with minimal issues such as GI distress or the product being too sweet to drink lots of it on hot days.</p>
                </div>
              </article>
            </div>
          </div>
        </div>
      </section>

        <hr className="hr-shine" />*/}

 

      {/* SECTION 3 */}
      <section className="sec belief">
        <div className="wrap" data-animate>
          <div className="belief-banner">
            <figure className="belief-figure parallax tilt"><SafeImg className="img" src="/Front-and-Back-Mock-Up.webp" /></figure>
            <aside className="belief-panel card">
              <div className="panel-accent" aria-hidden />
              <p >{/*className="measure"*/}
                At Dope Shot we believe in making a product based on trusted research that has been proven year after year to help athletes perform at their maximum level. MTC’s and 100 grams of carbohydrate intake per hour has been proven again and again. And Dope Shot is the easiest way to get both of these.
              </p>
            </aside>
          </div>
        </div>
      </section>

     <hr className="hr-shine" />

     {/* SECTION 4 — FAQ1 styled dropdowns */}
<section className="sec">
  <div className="wrap" data-animate>
    <h2 className="headline reveal">The importance of carbohydrate intake when not performing endurance events</h2>
        <br/>
    <div className="faq1-list slim">
      <FAQItem id="s4-q1" q="Train topped up, not on empty">
        <p>For best performance in training, you need to not be “running on empty”.  It is true that you want to train how you intend to perform.  Meaning you want to be “topped up” on your fuel level for every training day.  Using Dope Shot daily helps to let your gut familiarize itself with the product, making it easier for the ingested carbohydrates /dope shot to be used as energy (exogenous carbohydrate oxidation).</p>
      </FAQItem>

      <FAQItem id="s4-q2" q="Before training">
        <p>Prior to your workout, a couple shots of Dope Shot (100-200 calories) before exercise will have a positive effect on your performance and be super easy on your stomach for those times when you may be nervous about the event</p>
      </FAQItem>

      <FAQItem id="s4-q3" q="After training">
        <p>Post workout.  To get the most gains out of your workout or race, you need to supply your body immediately after the event.  This 30-60 minute window (glycogen window) is when your body will more easily absorb the fuel you consume.  This helps you be more ready for tomorrow’s workout or event.  Although you can continue to consume Dope Shot after your event or workout, I prefer to mix it up and drink juice, soda, Gatorade, etc.  Dope Shot is not intended to be the one and only way to get carbohydrates, it is a tool in your nutrition toolbox.</p>
      </FAQItem>

      <FAQItem id="s4-q4" q="Gut training and adaptation">
        <p>The concept of gut training moves the discussion beyond solely the product's formulation to encompass the athlete's physiological adaptation process. A product designed for high carbohydrate intake, such as Dope Shot, inherently necessitates and facilitates this gut training. This implies that the product's efficacy is not just a function of its ingredients but also depends on its consistent integration into an athlete's comprehensive training program. This perspective elevates the product from a simple supplement to a vital tool within a broader physiological adaptation strategy, aligning it with the practices of modern professional athletes who prioritize such adaptations for peak performance.</p>
      </FAQItem>
    </div>
  </div>
</section>


      <style>{`
        *,*::before,*::after{box-sizing:border-box}
        .dsl{--bg:#070a0f;--fg:#f5f7fb;--muted:#c6cddd;--line:rgba(255,255,255,.16);--line2:rgba(255,255,255,.10);--soft:rgba(255,255,255,.06);--card:#0e1420;--acc:#6fd1ff;
             color-scheme:dark;background:var(--bg);color:var(--fg);-webkit-font-smoothing:antialiased}
        .sec{padding:clamp(60px,7vw,120px) 0;position:relative}
        .wrap{max-width:1200px;margin:0 auto;padding-inline:clamp(16px,4vw,28px)}
        p{line-height:1.75;margin:0 0 12px;color:var(--muted);font-size:clamp(17px,1.15vw,19px);overflow-wrap:anywhere}
        .headline{margin:0 0 12px;font-size:clamp(22px,4.8vw,38px);letter-spacing:.2px;position:relative}
        .headline.sub{font-size:clamp(20px,3.8vw,30px)}
        .headline::after{content:"";position:absolute;left:0;bottom:-8px;height:2px;width:160px;background:linear-gradient(90deg,transparent,#fff,transparent);transform:scaleX(0);transform-origin:left center;transition:transform .6s ease}
        .headline.reveal-line::after{transform:scaleX(1)}
        .headline.shine::after{animation:lineShine 1.4s ease}
        @keyframes lineShine{0%{transform:scaleX(0)}60%{transform:scaleX(1)}100%{transform:scaleX(1)}}

        .page-progress{position:sticky;top:0;z-index:150;height:3px}
        .page-progress .bar{height:100%;width:100%;display:block;background:linear-gradient(90deg,transparent,#fff,transparent);transform:scaleX(0)}

        .bg{position:fixed;inset:0;z-index:-1;pointer-events:none}
        .bg-flow{position:absolute;inset:-20% -10%;background:
          radial-gradient(40% 30% at 15% 20%, rgba(111,209,255,.08), transparent 60%),
          radial-gradient(50% 35% at 80% 30%, rgba(139,92,246,.08), transparent 60%),
          linear-gradient(180deg, #06090e 0%, #070a0f 60%, #070a0f 100%);
          animation:bgDrift 50s ease-in-out infinite}
        @keyframes bgDrift{0%{transform:translate3d(0,0,0)}50%{transform:translate3d(2%, -1%,0)}100%{transform:translate3d(0,0,0)}}
        .bg-dots{position:absolute;inset:0;opacity:.14;background:
          radial-gradient(rgba(255,255,255,.06) 1px, transparent 1px) 0 0/18px 18px}
        .bg-noise{position:absolute;inset:0;mix-blend-mode:overlay;opacity:.10;background:
          radial-gradient(rgba(255,255,255,.025) .8px, transparent 1px) 0 0/3px 3px}

        .chips{display:flex;flex-wrap:wrap;gap:10px;align-items:center;margin:6px 0 14px}
        .chip{padding:8px 14px;border-radius:999px;background:linear-gradient(180deg,rgba(255,255,255,.12),rgba(255,255,255,.04));
              border:1px solid var(--line2);box-shadow:0 0 0 1px var(--soft) inset;font-weight:600;white-space:nowrap}

        .grid-2{display:grid;grid-template-columns:minmax(0,1.05fr) minmax(0,.95fr);gap:clamp(20px,4vw,40px);align-items:start}
        .grid-2.reverse{grid-template-columns:minmax(0,.95fr) minmax(0,1.05fr)}
        .col{min-width:0}
        .measure{max-width:74ch}

        /* ===== IMAGES — force full color (no B/W) ===== */
        .media-grid{display:grid;grid-template-columns:1fr 1fr;gap:14px}
        .media-grid .wide{grid-column:1/-1}
        .media-stack{display:grid;gap:14px}
        .ph{position:relative;border-radius:14px;overflow:hidden;background:transparent;border:1px solid var(--line2);box-shadow:0 10px 28px rgba(0,0,0,.35);aspect-ratio:16/9}
        .ph.tall{aspect-ratio:3/4}
        .img{width:100%;height:100%;object-fit:cover;display:block;image-rendering:auto;filter:none !important;-webkit-filter:none !important}
        :where(img){filter:none !important;-webkit-filter:none !important} /* hard kill any inherited filters */
        .img-fallback{background:#0f1520}

        /* glossy sweep on images */
        .ph::after,
        .belief-figure::after,
        .imp-thumb::after{
          content:""; position:absolute; inset:-25% -15%;
          background:linear-gradient(110deg, transparent 45%, rgba(255,255,255,.26) 50%, transparent 55%);
          transform:translateX(-130%) skewX(-18deg);
          opacity:0; pointer-events:none; mix-blend-mode:screen;
        }
        .sheen-run.ph::after,
        .sheen-run .imp-thumb::after,
        .sheen-run.belief-figure::after{animation:imgSheen 1.05s ease forwards}
        @keyframes imgSheen{0%{transform:translateX(-130%) skewX(-18deg);opacity:0}30%{opacity:.7}100%{transform:translateX(130%) skewX(-18deg);opacity:0}}
        .tilt:hover.ph{box-shadow:0 14px 36px rgba(0,0,0,.45)}

        /* ===== CARDS ===== */
        .card{
          --rx:0; --ry:0;
          background:linear-gradient(180deg,rgba(255,255,255,.06),rgba(255,255,255,.02));
          border:1px solid var(--line2);
          border-radius:16px;
          padding:clamp(14px,2vw,22px);
          box-shadow:0 10px 30px rgba(0,0,0,.35), 0 0 0 1px var(--soft) inset;
          transform:perspective(900px) rotateX(var(--rx)) rotateY(var(--ry));
          transition:transform .18s ease, box-shadow .18s ease, background .18s ease;
          position:relative; overflow:hidden;
        }
        .card::after{
          content:""; position:absolute; inset:-30% -10%;
          background:linear-gradient(105deg, transparent 35%, rgba(255,255,255,.18) 50%, transparent 65%);
          transform:translateX(-120%) skewX(-18deg);
          opacity:0; pointer-events:none;
        }
        .card.sheen-run::after{animation:sheen 1.05s ease forwards}
        @keyframes sheen{0%{transform:translateX(-120%) skewX(-18deg);opacity:0}30%{opacity:.6}100%{transform:translateX(120%) skewX(-18deg);opacity:0}}
        .tilt:hover{box-shadow:0 14px 36px rgba(0,0,0,.45), 0 0 0 1px var(--soft) inset}

        /* ===== IMPORTANCE (Section 4) ===== */
        .imp.grid{--stickTop:clamp(76px,9vh,120px);display:grid;grid-template-columns:84px 1fr;gap:20px;align-items:start;margin-top:14px}
        .imp-rail{position:sticky;top:var(--stickTop);height:calc(100vh - var(--stickTop));min-height:360px}
        .imp-rail::before{content:"";position:absolute;left:0;top:0;bottom:0;border-left:1px solid var(--line)}
        .imp-rail-fill{position:absolute;left:-1px;top:0;width:2px;height:100%;background:#fff;border-radius:2px;box-shadow:0 0 16px rgba(255,255,255,.35);transform:scaleY(0)}
        .imp-steps{display:grid;gap:clamp(16px,2vw,22px);min-width:0}
        .imp-step{display:grid;grid-template-columns:84px 1fr;gap:0 16px;align-items:start}
        .imp-dot{width:12px;height:12px;border-radius:50%;border:2px solid var(--fg);background:var(--bg);margin-left:28px;margin-top:10px;transition:transform .25s ease,background .25s ease,box-shadow .25s ease}
        .imp-step.is-active .imp-dot{background:var(--fg);transform:scale(1.12);box-shadow:0 0 18px rgba(255,255,255,.35)}
        .imp-card{display:grid;grid-template-columns:minmax(0,clamp(220px,26vw,360px)) minmax(0,1fr);gap:16px;align-items:start}
        .imp-thumb{aspect-ratio:4/3;border-radius:12px;overflow:hidden;background:transparent;border:1px solid var(--line2);box-shadow:0 10px 28px rgba(0,0,0,.35)}
        .imp-card .measure{max-width:68ch}

        .sec.alt{background:linear-gradient(180deg,rgba(255,255,255,.03),rgba(255,255,255,.01))}

        /* ===== Section 3 ===== */
        .belief-banner{display:grid;grid-template-columns:1fr;gap:clamp(14px,2.6vw,22px)}
        .belief-figure{position:relative;border-radius:20px;overflow:hidden;aspect-ratio:16/9;padding:0;background:transparent;border:1px solid var(--line2);box-shadow:0 10px 28px rgba(0,0,0,.35)}
        .belief-figure .img{width:100%;height:100%;object-fit:cover;display:block}
        .belief-panel{position:relative;padding:clamp(18px,2.6vw,26px);border-radius:18px;background:linear-gradient(180deg, rgba(255,255,255,.08), rgba(255,255,255,.02));backdrop-filter:blur(4px);box-shadow:0 10px 30px rgba(0,0,0,.35), 0 0 0 1px var(--soft) inset}
        .belief-panel .panel-accent{position:absolute; left:0; top:10px; bottom:10px; width:4px; border-radius:4px;background:linear-gradient(180deg, #fff, rgba(255,255,255,.35));box-shadow:0 0 16px rgba(255,255,255,.35)}


        @keyframes hrSweep{0%{transform:translateX(-40%)}100%{transform:translateX(40%)}}

        [data-animate] .reveal{will-change:transform,opacity,clip-path,filter}

        @media (max-width:1120px){.grid-2,.grid-2.reverse{grid-template-columns:1fr}}
        @media (max-width:900px){
          .imp.grid{grid-template-columns:72px 1fr}
          .imp-step{grid-template-columns:72px 1fr}
          .imp-card{grid-template-columns:minmax(0,200px) minmax(0,1fr)}
        }
        @media (max-width:620px){
          .media-grid{grid-template-columns:1fr}
          .imp.grid{grid-template-columns:56px 1fr}
          .imp-step{grid-template-columns:56px 1fr}
          .imp-card{grid-template-columns:1fr;gap:12px}
          .imp-thumb{aspect-ratio:16/9}
        }

    /* Let FAQ body text use full width inside the box */
    .faq1-a .measure { 
      max-width: none; 
      width: 100%;
    }


        html, body { -webkit-overflow-scrolling: touch; }
        @media (prefers-reduced-motion:reduce){
          .bg-flow, .card::after, .ph::after, .belief-figure::after, .hr-shine::after{animation:none!important}
        }
      `}</style>
    </main>
  );
}
